/* ============================================================
   MA Video Carousel
   - Regular carousel: native scroll + snap, prev/next/dots
   - Marquee mode: rAF-driven transform loop, pauses on hover
   - Video autoplay/pause controlled by IntersectionObserver
     (only slides in the scrollable viewport play)
   - Play button toggles individual video play/pause
   ============================================================ */

(function () {
  'use strict';

  class VideoCarousel extends HTMLElement {
    connectedCallback() {
      this.track     = this.querySelector('.ma-video-carousel__track');
      this.slides    = Array.from(this.querySelectorAll('.ma-video-carousel__slide'));
      this.dots      = Array.from(this.querySelectorAll('.ma-video-carousel__dot'));
      this.prevBtn   = this.querySelector('.ma-video-carousel__arrow--prev');
      this.nextBtn   = this.querySelector('.ma-video-carousel__arrow--next');

      this.desktopSlides = parseFloat(this.dataset.desktopSlides) || 5.5;
      this.mobileSlides  = parseFloat(this.dataset.mobileSlides)  || 1.5;
      this.isMarquee     = this.dataset.marquee === 'true';
      this.marqueeSpeed  = parseFloat(this.dataset.marqueeSpeed)  || 40;

      this.currentIndex = 0;

      this.intersectingSlides = new Set();

      this.applySlideVars();
      this.initModalOpeners();

      if (this.isMarquee) {
        this.initMarquee();
      } else {
        this.initCarousel();
      }

      // Defer all video work until the carousel section enters the viewport.
      // Without this gate, the lazy observer fires immediately (root=track, not viewport)
      // and injects <video> elements + starts HLS downloads for all slides on page load,
      // even when the carousel is far below the fold.
      const viewportObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          viewportObserver.disconnect();
          this.initLazyVideos();
          this.initVideoObserver();
        }
      }, { threshold: 0.1 });
      viewportObserver.observe(this);
    }

    disconnectedCallback() {
      if (this.rafId)         cancelAnimationFrame(this.rafId);
      if (this.lazyObserver)  this.lazyObserver.disconnect();
      if (this.videoObserver) this.videoObserver.disconnect();
    }

    // ── CSS custom properties ──────────────────────────────────────

    applySlideVars() {
      this.style.setProperty('--slides-desktop', this.desktopSlides);
      this.style.setProperty('--slides-mobile',  this.mobileSlides);
    }

    // ── Carousel mode ──────────────────────────────────────────────

    initCarousel() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.index, 10)));
      });

      this.track.addEventListener('scroll', () => this.onTrackScroll(), { passive: true });
      this.updateArrows();
      this.initDragScroll();
      this.initControlsVisibility();
    }

    // ── Hide controls when nothing to scroll ──────────────────────

    initControlsVisibility() {
      const controls = this.querySelector('.ma-video-carousel__controls');
      if (!controls) return;

      const check = () => {
        const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
        controls.hidden = !scrollable;
      };

      check();
      const ro = new ResizeObserver(check);
      ro.observe(this.track);
    }

    // ── Mouse drag-to-scroll ───────────────────────────────────────

    initDragScroll() {
      let isDragging = false;
      let startX = 0;
      let startScrollLeft = 0;
      let hasMoved = false;

      this.track.style.cursor = 'grab';

      this.track.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        isDragging = true;
        hasMoved = false;
        startX = e.pageX;
        startScrollLeft = this.track.scrollLeft;
        this.track.style.cursor = 'grabbing';
        this.track.style.scrollBehavior = 'auto';
        e.preventDefault();
      });

      window.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        const delta = e.pageX - startX;
        if (Math.abs(delta) > 4) hasMoved = true;
        this.track.scrollLeft = startScrollLeft - delta;
      });

      window.addEventListener('mouseup', () => {
        if (!isDragging) return;
        isDragging = false;
        this.track.style.cursor = 'grab';
        this.track.style.scrollBehavior = '';
        if (hasMoved) {
          const step = this.getSlideWidth() + this.getGap();
          const idx = Math.round(this.track.scrollLeft / step);
          this.goTo(idx);
        }
      });

      // Prevent click events from firing after a drag
      this.track.addEventListener('click', (e) => {
        if (hasMoved) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, true);
    }

    getSlideWidth() {
      return this.slides.length ? this.slides[0].offsetWidth : 0;
    }

    getGap() {
      return parseFloat(getComputedStyle(this.track).columnGap) || 12;
    }

    step(dir) {
      this.goTo(this.currentIndex + dir);
    }

    goTo(index) {
      index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.currentIndex = index;
      const offset = index * (this.getSlideWidth() + this.getGap());
      this.track.scrollTo({ left: offset, behavior: 'smooth' });
      this.updateDots();
      this.updateArrows();
    }

    onTrackScroll() {
      const step = this.getSlideWidth() + this.getGap();
      if (step === 0) return;
      const idx = Math.round(this.track.scrollLeft / step);
      if (idx !== this.currentIndex) {
        this.currentIndex = idx;
        this.updateDots();
        this.updateArrows();
      }
    }

    updateDots() {
      this.dots.forEach((dot, i) => {
        const active = i === this.currentIndex;
        dot.classList.toggle('ma-video-carousel__dot--active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        // Shrink dots far from active for large sets
        const dist = Math.abs(i - this.currentIndex);
        dot.classList.toggle('ma-video-carousel__dot--far', dist > 2);
      });
    }

    updateArrows() {
      if (this.prevBtn) {
        this.prevBtn.classList.toggle('ma-video-carousel__arrow--disabled', this.currentIndex === 0);
      }
      if (this.nextBtn) {
        this.nextBtn.classList.toggle('ma-video-carousel__arrow--disabled', this.currentIndex >= this.slides.length - 1);
      }
    }

    // ── Marquee mode ───────────────────────────────────────────────

    initMarquee() {
      // Duplicate slides for seamless loop
      const originals = this.slides.slice();
      originals.forEach((s) => {
        const clone = s.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
      });

      this.marqueePos   = 0;
      this.marqueePaused = false;
      this.lastTs        = null;

      // Pause on hover
      this.addEventListener('mouseenter', () => { this.marqueePaused = true;  });
      this.addEventListener('mouseleave', () => { this.marqueePaused = false; });

      this.rafId = requestAnimationFrame((ts) => this.tickMarquee(ts));
    }

    getOriginalTrackWidth() {
      // Width of the original (non-cloned) slides including gaps
      return this.slides.reduce((sum, s) => sum + s.offsetWidth + this.getGap(), 0);
    }

    tickMarquee(ts) {
      if (this.lastTs !== null && !this.marqueePaused) {
        const delta = ts - this.lastTs;
        this.marqueePos += (this.marqueeSpeed * delta) / 1000;
        const loopAt = this.getOriginalTrackWidth();
        if (loopAt > 0 && this.marqueePos >= loopAt) {
          this.marqueePos -= loopAt;
        }
        this.track.style.transform = `translateX(${-this.marqueePos}px)`;
      }
      this.lastTs = ts;
      this.rafId = requestAnimationFrame((ts) => this.tickMarquee(ts));
    }

    // ── Lazy video loading ─────────────────────────────────────────

    initLazyVideos() {
      // Pre-inject <video> elements for slides near the visible area of the track.
      // Called only after the section enters the viewport (see viewportObserver in connectedCallback).
      this.lazyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activateSlide(entry.target);
            this.lazyObserver.unobserve(entry.target);
          }
        });
      }, {
        root: this.isMarquee ? null : this.track,
        rootMargin: '0px 200% 0px 200%',
      });

      this.slides.forEach((slide) => this.lazyObserver.observe(slide));
    }

    activateSlide(slide) {
      if (slide.dataset.activated) return;
      slide.dataset.activated = 'true';

      const tpl = slide.querySelector('.ma-video-carousel__video-tpl');
      if (!tpl) return;

      const video = tpl.content.querySelector('video').cloneNode(true);
      const wrapper = slide.querySelector('.ma-video-carousel__video-wrapper');
      const playBtn = slide.querySelector('.ma-video-carousel__play-btn');

      // Insert video above poster, below play button
      wrapper.insertBefore(video, playBtn);

      // Sync play button visibility with video state
      video.addEventListener('play',  () => playBtn?.classList.add('ma-video-carousel__play-btn--hidden'));
      video.addEventListener('pause', () => playBtn?.classList.remove('ma-video-carousel__play-btn--hidden'));

      // If this slide is already in the visible viewport, autoplay immediately
      if (this.intersectingSlides.has(slide) && slide.dataset.autoplay === 'true' && !slide.dataset.userPaused) {
        video.play().catch(() => {});
      }
    }

    // ── Video observer (play videos in view, pause out of view) ───

    initVideoObserver() {
      const root      = this.isMarquee ? null : this.track;
      const threshold = 0.55;

      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const slide = entry.target;

          if (entry.isIntersecting) {
            this.intersectingSlides.add(slide);
            const video = slide.querySelector('.ma-video-carousel__video');
            if (video && slide.dataset.autoplay === 'true' && !slide.dataset.userPaused) {
              video.play().catch(() => {});
            }
          } else {
            this.intersectingSlides.delete(slide);
            const video = slide.querySelector('.ma-video-carousel__video');
            if (video && !video.paused) {
              video.pause();
            }
          }
        });
      }, { root, threshold });

      this.slides.forEach((slide) => this.videoObserver.observe(slide));
    }

    // ── Video modal ────────────────────────────────────────────────

    initModalOpeners() {
      this.modal = this.querySelector('.ma-vcm');
      if (!this.modal) return;

      const videoWrap = this.modal.querySelector('.ma-vcm__video-wrap');
      const closeBtn  = this.modal.querySelector('.ma-vcm__close');

      // Clicking any video wrapper opens the modal
      this.track.addEventListener('click', (e) => {
        const wrapper = e.target.closest('.ma-video-carousel__video-wrapper');
        if (!wrapper) return;
        const slide = wrapper.closest('.ma-video-carousel__slide');
        if (!slide) return;
        this.openModal(slide, wrapper);
      });

      // Close button
      closeBtn?.addEventListener('click', () => this.modal.close());

      // Click on backdrop (outside the inner dialog box) closes it
      this.modal.addEventListener('click', (e) => {
        const rect = this.modal.getBoundingClientRect();
        if (
          e.clientX < rect.left || e.clientX > rect.right ||
          e.clientY < rect.top  || e.clientY > rect.bottom
        ) {
          this.modal.close();
        }
      });

      // Teardown on close (ESC, close button, or backdrop click)
      this.modal.addEventListener('close', () => {
        const v = videoWrap?.querySelector('video');
        if (v) { v.pause(); v.remove(); }
        this._modalOpener?.focus();
        this._modalOpener = null;
      });
    }

    openModal(slide, opener) {
      const tpl = slide.querySelector('.ma-video-carousel__video-tpl');
      if (!tpl || !this.modal) return;

      const videoWrap = this.modal.querySelector('.ma-vcm__video-wrap');

      // Pause the carousel's inline video if it's playing
      const inline = slide.querySelector('.ma-video-carousel__video');
      if (inline && !inline.paused) inline.pause();

      // Build modal video from the slide's template sources
      const tplVid = tpl.content.querySelector('video');
      const v = document.createElement('video');
      v.controls   = true;
      v.playsInline = true;
      v.preload    = 'auto';
      tplVid.querySelectorAll('source').forEach((s) => v.appendChild(s.cloneNode(true)));

      videoWrap.innerHTML = '';
      videoWrap.appendChild(v);

      this._modalOpener = opener;
      this.modal.showModal();
      v.play().catch(() => {});
    }
  }

  customElements.define('video-carousel', VideoCarousel);
})();
